// Création du composant Sortable Item
import { useSortable } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { ArrowDown, ArrowUp, GripVertical } from "lucide-react";
import { Button } from "@repo/ui";
import { useIsMobile } from "@repo/ui";

interface SortableItemProps {
  id: string;
  label: string;
  index: number; // Position de l'item (0, 1, 2)
  total: number; // Nombre d'items dans la liste
  editable?: boolean;
  /** Vrai juste après un déplacement : déclenche le flash de confirmation. */
  highlighted?: boolean;
  onMove?: (fromIndex: number, toIndex: number) => void;
}

// Fonction pour obtenir la classe du badge selon la position
const getPositionBadgeClass = () => {
  return "bg-muted text-foreground border-border";
};

const getPositionLabel = (index: number) => index + 1;

interface ItemCardProps {
  label: string;
  index: number;
  total: number;
  editable: boolean;
  /** État visuel : `overlay` = copie suivie par le doigt/curseur. */
  state?: "idle" | "over" | "overlay";
  highlighted?: boolean;
  onMove?: (fromIndex: number, toIndex: number) => void;
  handleProps?: Record<string, unknown>;
}

/** Rendu pur de la carte, partagé par SortableItem et le DragOverlay. */
export function PamiItemCard({
  label,
  index,
  total,
  editable,
  state = "idle",
  highlighted = false,
  onMove,
  handleProps,
}: ItemCardProps) {
  // Sur mobile le drag & drop est désactivé : réordonnancement via les flèches
  // uniquement (le geste tactile entrait en conflit avec le scroll de page).
  const isMobile = useIsMobile();
  const dragEnabled = editable && !isMobile;

  return (
    <div
      className={`flex items-center gap-3 p-3 sm:p-4 bg-card rounded-surface shadow-sm transition-all duration-200 ${
        state === "overlay"
          ? "border-2 border-primary shadow-xl ring-2 ring-primary/30 cursor-grabbing"
          : state === "over"
            ? "border-2 border-primary bg-primary/5 shadow-md"
            : highlighted
              ? // Flash de confirmation après un déplacement par les flèches.
                "border border-primary bg-primary/5 ring-2 ring-primary/30"
              : "border border-border hover:border-accent hover:shadow-md"
      }`}
    >
      {/* Badge numéroté de position */}
      <div
        className={`shrink-0 w-10 h-10 rounded-full font-bold text-lg flex items-center justify-center border ${getPositionBadgeClass()}`}
      >
        {getPositionLabel(index)}
      </div>

      {/* Le "handle" pour glisser l'élément. 44px au tactile (cible minimum),
          touch-action: none pour que le navigateur ne préempte pas le geste. */}
      {dragEnabled && (
        <Button
          type="button"
          variant="ghost"
          size="icon"
          aria-label={`Déplacer « ${label} »`}
          // WebkitTouchCallout : sans ça iOS ouvre la loupe de sélection sur
          // l'appui long au lieu de laisser dnd-kit démarrer le drag.
          style={{
            touchAction: "none",
            WebkitTouchCallout: "none",
            userSelect: "none",
          }}
          className="shrink-0 cursor-grab active:cursor-grabbing h-11 w-11 hover:bg-accent/50 transition-colors p-0"
          {...handleProps}
        >
          <GripVertical className="text-muted-foreground size-6" />
        </Button>
      )}

      {/* Texte de l'affirmation */}
      <span className="text-base sm:text-lg select-none flex-1 leading-snug">
        {label}
      </span>

      {/* Flèches monter/descendre, en complément du drag & drop (handle ci-dessus).
          Flèches pleines plutôt que chevrons : empilés dans une carte, les
          chevrons se lisent comme un « déplier/replier ». Séparateur vertical
          pour détacher la zone d'action du texte, et état désactivé franc
          (opacité 30 %) pour signaler qu'on est déjà en 1re / dernière position. */}
      {editable && state !== "overlay" && (
        <div className="flex shrink-0 flex-col gap-1 border-l border-border pl-2">
          <Button
            type="button"
            variant="outline"
            size="icon"
            aria-label={`Monter « ${label} » en position ${index}`}
            title="Monter"
            className="h-8 w-8 p-0 disabled:opacity-30 disabled:cursor-not-allowed"
            disabled={index === 0}
            onClick={() => onMove?.(index, index - 1)}
          >
            <ArrowUp className="size-4" />
          </Button>
          <Button
            type="button"
            variant="outline"
            size="icon"
            aria-label={`Descendre « ${label} » en position ${index + 2}`}
            title="Descendre"
            className="h-8 w-8 p-0 disabled:opacity-30 disabled:cursor-not-allowed"
            disabled={index === total - 1}
            onClick={() => onMove?.(index, index + 1)}
          >
            <ArrowDown className="size-4" />
          </Button>
        </div>
      )}
    </div>
  );
}

export function SortableItem({
  id,
  label,
  index,
  total,
  editable = true,
  highlighted = false,
  onMove,
}: SortableItemProps) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
    isSorting,
    isOver,
  } = useSortable({
    id,
    // Sans ça, un réordonnancement déclenché hors drag (flèches monter/descendre)
    // fait animer la carte depuis son ancienne position mesurée : elle « vole »
    // depuis le haut du conteneur. Les positions sont déjà à jour dans le DOM,
    // seule l'animation de dnd-kit est parasite ici.
    animateLayoutChanges: () => false,
  });

  // transform/transition uniquement pendant un drag : en dehors, c'est le DOM
  // réordonné par React qui fait foi.
  const style = isSorting
    ? {
        transform: CSS.Transform.toString(transform),
        transition: transition ?? undefined,
        // L'original reste en place, estompé : la copie visible est le DragOverlay.
        opacity: isDragging ? 0.4 : 1,
      }
    : undefined;

  return (
    <div ref={setNodeRef} style={style}>
      <PamiItemCard
        label={label}
        index={index}
        total={total}
        editable={editable}
        state={isOver && !isDragging ? "over" : "idle"}
        highlighted={highlighted}
        onMove={onMove}
        handleProps={{ ...attributes, ...listeners }}
      />
    </div>
  );
}
